import * as React from 'react';
import { styled } from '@glitz/react';
import { translate } from '@avensia/excite';
import { red } from 'nordicnest/shared/theme';
import { useCustomerType } from '@avensia/excite-commercetools';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { translationExists } from 'webapp/utils/translations';
import { Variant } from 'webapp/product/price-variant';
import { useFormatPrice } from '@avensia/excite-ui';

export type PriceConfig = {
  showRedPrice?: boolean;
  showComparePricePlp?: boolean;
  showComparePriceType?: NordicNest.Api.ComparePriceType;
  showComparePriceStrikethrough?: boolean;
  showComparePriceSuffix?: boolean;
  lowestHistoricalPrice?: Excite.Commerce.AmountWithVatApiModel;
  showComparePricePlpType?: NordicNest.Api.ComparePricePlpType;
  showOriginalPrice?: boolean;
};

type PropType = {
  price: NordicNest.Api.PriceApiModel | Excite.Commercetools.CartPriceApiModel;
  additionalPrice?: number;
  variant?: Variant;
  config?: PriceConfig;
  alwaysShowWithVat?: boolean;
  showPlusSign?: boolean;
};

const Price = styled(
  ({ price, variant = Variant.Default, config, additionalPrice, alwaysShowWithVat, showPlusSign }: PropType) => {
    const baseStyle = React.useMemo(() => {
      if (variant === Variant.Default) {
        return styled({
          flexDirection: 'column',
          justifyContent: 'left',
          fontSize: t => t.fontSize.s,
        });
      } else {
        return styled({
          flexDirection: variant === Variant.CompactReverse ? 'column-reverse' : undefined,
          fontSize: t => t.fontSize.xxs,
        });
      }
    }, [variant]);

    const isDefaultVariant = variant === Variant.Default;
    const { isCompanyCustomer } = useCustomerType();
    const formatPrice = useFormatPrice();

    const shouldShowOriginalPrefixSuffix =
      config?.showComparePricePlpType === 'originalPrice' ||
      (config?.showComparePricePlpType === 'inherit' && config?.showComparePriceType === 'originalPrice');

    let originalPricePrefix =
      !isDefaultVariant &&
      config?.showComparePriceSuffix &&
      shouldShowOriginalPrefixSuffix &&
      translate(t => t.productListing.originalPricePrefix);
    if (originalPricePrefix && !translationExists(originalPricePrefix)) {
      originalPricePrefix = '';
    }
    let originalPriceSuffix =
      !isDefaultVariant &&
      config?.showComparePriceSuffix &&
      shouldShowOriginalPrefixSuffix &&
      translate(t => t.productListing.originalPriceSuffix);
    if (originalPriceSuffix && !translationExists(originalPriceSuffix)) {
      originalPriceSuffix = '';
    }

    let comparePrice =
      config?.showComparePriceType === 'originalPrice' ? price?.regular : config?.lowestHistoricalPrice;

    if (config?.showComparePricePlpType === 'originalPrice') {
      comparePrice = price?.regular;
    }

    if (config?.showComparePricePlpType === 'referencePrice') {
      comparePrice = config?.lowestHistoricalPrice;
    }

    return (
      <Base css={baseStyle}>
        <CurrentPrice
          css={config?.showRedPrice && redPriceDecorator}
          {...(config?.showRedPrice ? NnTestIdProps.RedPriceElement : NnTestIdProps.CurrentPriceElement)}
          suppressHydrationWarning
        >
          {showPlusSign ? '+' : ''}
          {formatPrice(
            isCompanyCustomer && !alwaysShowWithVat
              ? price.current.exclVat + (additionalPrice ?? 0)
              : price.current.inclVat + (additionalPrice ?? 0),
          )}
        </CurrentPrice>
        {config?.showComparePricePlp && comparePrice && (
          <OriginalPrice css={variant === Variant.Default && originalPriceDecorator}>
            {Boolean(originalPricePrefix) && (
              <OriginalPricePrefixSuffix>{originalPricePrefix + ' '}</OriginalPricePrefixSuffix>
            )}
            <OriginalPriceSum
              css={config?.showComparePriceStrikethrough && !config?.showOriginalPrice && comparePriceSumDecorator}
              suppressHydrationWarning
              {...NnTestIdProps.ComparePriceElement}
            >
              {isCompanyCustomer && !alwaysShowWithVat
                ? formatPrice(comparePrice.exclVat)
                : formatPrice(comparePrice.inclVat)}
            </OriginalPriceSum>
            {Boolean(originalPriceSuffix) && (
              <OriginalPricePrefixSuffix {...NnTestIdProps.OriginalPricePrefixSuffix}>
                {' ' + originalPriceSuffix}
              </OriginalPricePrefixSuffix>
            )}
          </OriginalPrice>
        )}
        {isDefaultVariant && (
          <VatText>
            {config?.showComparePricePlp &&
              config?.showComparePriceSuffix &&
              (isCompanyCustomer && !alwaysShowWithVat
                ? translate(t => t.order.recommendedRetailPriceExclVat)
                : translate(t => t.order.recommendedRetailPriceInclVat))}
          </VatText>
        )}
      </Base>
    );
  },
);

export default Price;

const originalPriceDecorator = styled({
  fontSize: 19,
});

const redPriceDecorator = styled({
  color: red,
});

const Base = styled.div({
  columnGap: t => t.spacing.xs,
  display: 'flex',
});

const CurrentPrice = styled.span({
  color: 'black',
  fontWeight: 'bold',
});

const OriginalPrice = styled.span({
  color: t => t.dark.color,
  fontWeight: 400,
  opacity: 0.75,
});

const OriginalPriceSum = styled.span();

const comparePriceSumDecorator = styled({
  textDecoration: 'line-through',
});

const OriginalPricePrefixSuffix = styled.span({
  textDecoration: 'none',
});

const VatText = styled.span({
  color: t => t.dark.color,
  fontWeight: t => t.price?.vatText?.fontWeight,
  opacity: 0.75,
});
